/* eslint-disable react-hooks/rules-of-hooks */

import React, { createContext, useContext, useEffect, useState } from "react";
import { Linking, Platform } from "react-native";
import { v4 as uuidv4 } from "uuid";

import {
  signIn as signInAmplify,
  confirmSignIn,
  signUp as signUpAmplify,
  signOut as signOutAmplify,
  confirmResetPassword,
  autoSignIn,
  decodeJWT,
} from "aws-amplify/auth";
import { Amplify } from "aws-amplify";

import AsyncStorage from "@react-native-async-storage/async-storage";

import analytics from "../analytics";
import branch, { initBranch } from "../branchlib";
import { fetchProfile } from "./http/events";
import jwtDecode from "jwt-decode";
import Sentry from "../sentry";
import { fetchAuthSessionWithLoginMismatchRetry } from "../utils/amplifyAuthRecovery";
import {
  deviceLanguage,
  getLanguage,
  isEnglish,
  t,
} from "@/localization";
import { setWorkspaceSelf } from "./teams/workspace";
import { restoreWorkspace } from "./teams/actions";
import { deriveWorkspace, useWorkspaceState } from "./teams/useWorkspace";

const authContext = createContext();

const stringifyValues = (clientMetadata) =>
  Object.fromEntries(
    Object.entries(clientMetadata).map(([key, value]) => [
      key,
      value.toString(),
    ])
  );

// Cognito's and Amplify's most common messages on the sign-in, sign-up and
// password screens, translated. Others stay as Cognito wrote them.
const translatedAuthError = (message) => {
  switch (message) {
    case "Incorrect username or password.":
      return t("Incorrect username or password.");
    case "Password attempts exceeded":
      return t("Password attempts exceeded");
    case "User does not exist.":
      return t("User does not exist.");
    case "Username/client id combination not found.":
      return t("Username/client id combination not found.");
    case "User is disabled.":
      return t("User is disabled.");
    case "Attempt limit exceeded, please try after some time.":
      return t("Attempt limit exceeded, please try after some time.");
    case "Invalid verification code provided, please try again.":
      return t("Invalid verification code provided, please try again.");
    case "Invalid code provided, please request a code again.":
      return t("Invalid code provided, please request a code again.");
    case "Password did not conform with policy: Password not long enough":
      return t(
        "Password did not conform with policy: Password not long enough"
      );
    case "Password does not conform to policy: Password not long enough":
      return t("Password does not conform to policy: Password not long enough");
    case "Cannot reset password for the user as there is no registered/verified email or phone_number":
      return t(
        "Cannot reset password for the user as there is no registered/verified email or phone_number"
      );
    case "A network error has occurred.":
      return t("A network error has occurred.");
    default:
      return null;
  }
};

// The text to show for a failed sign-in, sign-up or password reset. English
// shows `fallback` exactly as before: by default Cognito's own message.
export const authErrorMessage = (error, fallback = error?.message) =>
  (!isEnglish() && translatedAuthError(error?.message)) || fallback;

function useProvideAuth(navigation, tokens) {
  const [branchdata, setbranchdata] = useState({});

  useEffect(() => {
    (async () => {
      const data = await initBranch();
      setbranchdata(data);
    })();
  }, []);

  // TODO: CONDITIONAL IMPORT
  // useEffect(() => {
  //   if (Platform.OS !== "web") {
  //     const { appleAuth } = require("@invertase/react-native-apple-authentication");
  //     return appleAuth.onCredentialRevoked(async () => {
  //       await signOut();
  //     });
  //   }
  // }, []);

  const initialState = {
    broadcaster: "",
    isLoading: true,
    isSignedOut: false,
    userToken: null,
    isSigningInWithTokens: false,
    profileLoaded: false,
  };

  const [state, dispatch] = React.useReducer((prevState, action) => {
    switch (action.type) {
      case "RESTORE_TOKEN":
        return {
          ...prevState,
          userToken: action.token,
          userEmail: action.userEmail,
          onboarding_done: action.onboarding_done,
          firstLoad: action.firstLoad,
          isLoading: false,
        };
      case "SET_FIRST_LOAD":
        return {
          ...prevState,
          firstLoad: action.firstLoad,
        };
      case "SIGN_IN":
        return {
          ...prevState,
          isSignedOut: false,
          userToken: action.token,
          isLoading: false,
          isSigningInWithTokens: false,
        };
      case "SIGN_OUT":
        return {
          ...initialState,
          isSignedOut: true,
          isLoading: false,
        };
      case "SET_PROFILE":
        return {
          ...prevState,
          broadcaster: action.attributes["custom:broadcaster"],
          name: action.attributes.name,
          email: action.attributes.email,
          sub: action.attributes.sub,
        };
      case "SIGN_UP":
        return {
          ...prevState,
          onboarding_done: false,
        };
      case "ONBOARDING_DONE":
        return {
          ...prevState,
          onboarding_done: true,
        };
      case "ENRICH_PROFILE":
        return {
          ...prevState,
          ...action.payload,
          profileLoaded: true,
        };
      case "SET_SIGNING_IN_WITH_TOKENS":
        return {
          ...prevState,
          isSigningInWithTokens: action.value,
        };
    }
  }, initialState);

  // Teams: the workspace store learns who's signed in before the profile
  // lands, so a reload's first requests already name the remembered account
  // (components/teams/workspace.js). No-ops while the Teams UI is off.
  const setProfileAttributes = (attributes) => {
    setWorkspaceSelf(attributes?.["custom:broadcaster"]);
    dispatch({ type: "SET_PROFILE", attributes });
  };
  const dispatchSignOut = () => {
    setWorkspaceSelf(null);
    dispatch({ type: "SIGN_OUT" });
  };

  const workspaceState = useWorkspaceState();
  const workspace = React.useMemo(
    () => deriveWorkspace(workspaceState),
    [workspaceState]
  );
  // Once signed in: check the remembered account with the server and load
  // what's shared with this user.
  useEffect(() => {
    if (
      workspaceState.enabled &&
      workspaceState.self &&
      workspaceState.restored &&
      !state.isSignedOut
    ) {
      restoreWorkspace();
    }
  }, [
    workspaceState.enabled,
    workspaceState.self,
    workspaceState.restored,
    state.isSignedOut,
  ]);

  const refetchProfile = async () => {
    const profile = await fetchProfile();
    const { events, ...payload } = profile;

    payload.publishedEvents = events.filter(
      (e) => !e.is_test && e.is_published
    ).length;

    payload.unpublishedEvents = events.filter(
      (e) => !e.is_test && !e.is_published
    ).length;

    payload.testEvents = events.filter((e) => e.is_test).length;

    // app_language: the language the app shows (en, es-MX, pt-PT…); the
    // device's own language goes with every event as "language".
    analytics.identify(profile.username, {
      ...payload,
      app_language: getLanguage(),
    });

    dispatch({ type: "ENRICH_PROFILE", payload });

    return payload;
  };

  React.useEffect(() => {
    // Fetch the token from storage then navigate to our appropriate place
    const bootstrapAsync = async () => {
      try {
        if (tokens.idToken) {
          // Perform windowSignIN
          await signInWithTokens(tokens);
        } else {
          const userToken = await AsyncStorage.getItem("userToken");

          const onboarding_done =
            (await AsyncStorage.getItem("onboarding_done")) === "false"
              ? false
              : null;
          const userEmail = (await AsyncStorage.getItem("userEmail")) || "";
          if (!userToken) {
            throw new Error("Token not found");
          }

          const fl = await AsyncStorage.getItem("firstLoad");

          const firstLoad = fl !== "false" ? true : false;

          dispatch({
            type: "RESTORE_TOKEN",
            token: userToken,
            userEmail,
            onboarding_done,
            firstLoad,
          });

          // const { username, userId } = await getCurrentUser({
          //   bypassCache: false, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
          // });
          // if (!username) {
          //   throw new Error("Sign In expired");
          // }

          const session = await fetchAuthSessionWithLoginMismatchRetry({
            bypassCache: false, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
          });

          if (!session.userSub) {
            throw new Error("Sign In expired");
          }

          // IDENTIFY user as early as possible
          Sentry.setUser({
            id: session.tokens?.idToken?.payload["custom:broadcaster"],
          });
          // Get broadcaster
          setProfileAttributes(session.tokens?.idToken?.payload);

          await refetchProfile(session.tokens?.idToken?.payload || {});
        }
      } catch (e) {
        console.error(e);
        dispatchSignOut();
      }
    };

    bootstrapAsync();
  }, [tokens.idToken]);

  const setFirstLoad = async (firstLoad) => {
    await AsyncStorage.setItem("firstLoad", firstLoad ? "true" : "false");
    dispatch({
      type: "SET_FIRST_LOAD",
      firstLoad,
    });
  };

  const checkUserExists = async (email) => {
    // console.log("CHECKING USER EXISTS", email);
    try {
      await AsyncStorage.setItem("userEmail", email.toLowerCase());
      await signOutAmplify();
      await signInAmplify({
        username: email.toLowerCase(),
        password: "random",
        options: {
          authFlowType: "USER_SRP_AUTH",
        },
      });

      return true;
    } catch (error) {
      // console.log("ERROR:", error, error.code);
      if (error.toString().indexOf("UserNotFoundException") !== -1) {
        return false;
      } else {
        return true;
      }
      // throw error;
    }
  };

  const signInAndFillProfile = async (email) => {
    const session = await fetchAuthSessionWithLoginMismatchRetry({
      bypassCache: false, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });

    // console.log("SESSION:", session);
    const attributes = session.tokens?.idToken?.payload;
    // console.log(attributes);
    const token = session.tokens?.idToken?.payload?.sub;

    await AsyncStorage.setItem("userToken", token);
    // console.log("SETTING USER EMAIL", email);
    await AsyncStorage.setItem("userEmail", email);

    dispatch({ type: "SIGN_IN", token });
    setProfileAttributes(attributes);
    Sentry.setUser({ id: attributes["custom:broadcaster"] });

    const profile = await refetchProfile(attributes || {});
    return profile;
  };

  const signIn = async ({ email, password }) => {
    // console.log("SIGNING IN", email, password);
    try {
      await signOutAmplify();
      const r = await signInAmplify({
        username: email.toLowerCase(),
        password,
        platform: Platform.OS,
        options: {
          authFlowType: "USER_SRP_AUTH",
        },
      });

      // console.log("r", r);

      const profile = await signInAndFillProfile(email.toLowerCase());

      await analytics.track("Sign In", {
        platform: Platform.OS,
      });

      return profile;
    } catch (err) {
      console.log("error in sign in");
      console.log(err.underlyingError);
      console.error(err);
      throw err;
    }
  };

  const signInWithSocial = async (result, clientMetadata) => {
    console.log("RESULT:", result);
    console.log("CLIENT METADATA:", clientMetadata);

    const decoded = jwtDecode(result.identityToken);
    console.log("DECODED:", decoded);
    // console.log("DECODED:", decoded);

    let res;
    try {
      await signOutAmplify();
      res = await signInAmplify({
        username: decoded.email.toLowerCase(),
        options: {
          authFlowType: "CUSTOM_WITHOUT_SRP",
        },
      });

      console.log("res", res);

      if (
        res.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
      ) {
        // console.log("CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE");
        const r = await confirmSignIn({
          // res,
          challengeResponse: JSON.stringify({
            idToken: result.identityToken,
            nonce: decoded.nonce, //? weird why do i need result.nonce?
          }),
          // options: {
          //   authFlowType: "CUSTOM_WITHOUT_SRP",
          // },
        });
        // console.log("r", r);
      }

      const profile = await signInAndFillProfile(decoded.email.toLowerCase());

      await analytics.track("Sign In", {
        platform: Platform.OS,
        via: "social",
      });

      return profile;
    } catch (err) {
      console.log("error in sign in");
      console.error(err);
      if (err.toString().indexOf("UserNotFoundException") !== -1) {
        console.log("signing up");
        const profile = await signUp(
          {
            email: decoded.email,
            password: generateStrongPassword(),
            firstname: result.fullName.givenName,
            lastname: result.fullName.familyName,
          },
          {
            method: "social",
            iss: decoded.iss,
            ...stringifyValues(clientMetadata),
          }
        );
        return profile;
      } else {
        throw err;
      }
    }
  };

  const signInWithTokens = async ({ idToken, accessToken, username }) => {
    console.log("SIGNING IN WITH TOKENS", {
      idToken: idToken ? "***" : undefined,
      accessToken: accessToken ? "***" : undefined,
      username,
    });

    // Set loading state
    dispatch({ type: "SET_SIGNING_IN_WITH_TOKENS", value: true });

    try {
      // Decode the tokens to extract user information
      const decodedIdToken = decodeJWT(idToken);
      const decodedAccessToken = decodeJWT(accessToken);

      // Validate tokens
      const now = Math.floor(Date.now() / 1000);
      if (
        decodedIdToken.payload.exp < now ||
        decodedAccessToken.payload.exp < now
      ) {
        const expired = new Error("Tokens are expired");
        expired.code = "TOKENS_EXPIRED";
        throw expired;
      }

      // Extract user attributes from ID token
      const attributes = decodedIdToken.payload;
      const userSub = attributes.sub;
      const email = attributes.email;
      const broadcaster = attributes["custom:broadcaster"];

      if (!userSub || !email) {
        throw new Error("Invalid token payload");
      }

      // Sign out any existing session
      await signOutAmplify();

      // Sign in using CUSTOM_WITHOUT_SRP flow
      const res = await signInAmplify({
        username: email.toLowerCase(),
        options: {
          authFlowType: "CUSTOM_WITHOUT_SRP",
        },
      });

      console.log("Sign in result:", res);

      if (
        res.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
      ) {
        console.log("Confirming sign in with custom challenge");

        // Pass tokens as challenge response with authType flag
        const challengeResponse = JSON.stringify({
          idToken,
          accessToken,
          authType: "TOKEN_AUTH", // Flag to identify this as token-based auth
        });

        const confirmResult = await confirmSignIn({
          challengeResponse,
        });

        console.log("Confirm sign in result:", confirmResult);

        if (confirmResult.isSignedIn) {
          // Sign in successful, now fill profile
          const profile = await signInAndFillProfile(email.toLowerCase());

          await analytics.track("Sign In", {
            platform: Platform.OS,
            via: "tokens",
          });

          console.log("Successfully signed in with tokens");

          // Clear loading state (will be cleared by SIGN_IN action too)
          dispatch({ type: "SET_SIGNING_IN_WITH_TOKENS", value: false });

          return profile;
        } else {
          throw new Error("Failed to confirm sign in");
        }
      } else {
        throw new Error("Unexpected sign in step: " + res.nextStep?.signInStep);
      }
    } catch (error) {
      console.error("Error signing in with tokens:", error);
      dispatch({ type: "SET_SIGNING_IN_WITH_TOKENS", value: false });
      // Expired tokens fail before anything changed: whoever was signed in
      // stays signed in.
      if (error?.code !== "TOKENS_EXPIRED") dispatchSignOut();
      throw error;
    }
  };

  const signOut = async () => {
    await signOutAmplify();
    analytics.track("Sign Out");
    analytics.reset();
    // Cache.setItem("events", []);
    await AsyncStorage.setItem("userToken", "");
    await AsyncStorage.setItem("onboarding_done", "false");
    await AsyncStorage.removeItem("onboardingData");
    await AsyncStorage.removeItem("reviewAsked");

    dispatchSignOut();
  };

  const signUp = async (
    { email, password, firstname, lastname },
    signUpMetadata
  ) => {
    // Every sign-up (email, Apple, Google; web and apps) keeps the language
    // the app showed and the device's own with the account (the broadcaster
    // row's meta, lambdaHookPostConfirmation), for emails in that language.
    const device = deviceLanguage();
    const clientMetadata = {
      ...signUpMetadata,
      language: getLanguage(),
      // stringifyValues needs a value: left out when the device doesn't say.
      ...(device && { device_language: device }),
    };
    try {
      const username = uuidv4();

      let params = {
        username,
        password,
        options: {
          userAttributes: {
            email: email.toLowerCase(),
            name: `${firstname} ${lastname}`,
            ...(firstname && { given_name: firstname }),
            ...(lastname && { family_name: lastname }),
            // 'custom:broadcaster': username,
          },
          autoSignIn: true,
          // {
          //   authFlowType: "USER_AUTH",
          // },
          // autoSignIn: true,
          validationData: [],
          clientMetadata: stringifyValues(clientMetadata),
        },
      };

      // console.log("params signUpAmplify", params);

      const res = await signUpAmplify(params);
      // console.log("res signUpAmplify", res);

      if (res.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        // console.log("COMPLETE_AUTO_SIGN_IN, autoSignIn");
        const r = await autoSignIn();
      }

      // console.log("Sign up done");
      // console.log(user);
      dispatch({ type: "SIGN_UP" });

      // console.log(clientMetadata);

      if (!clientMetadata?.onboarding_done) {
        await AsyncStorage.setItem("onboarding_done", "false");
      }

      const profile = await signInAndFillProfile(email.toLowerCase());

      await analytics.track("Sign Up", {
        platform: Platform.OS,
        method: "email_password",
        email,
        first_name: firstname,
        last_name: lastname,
        ...clientMetadata, // signUpWithSocial will overwrite method
      });

      return profile;
    } catch (err) {
      // do something here\
      console.error(err);
      throw new Error(err && err.message);
    }
  };

  const finishOnboarding = async (values) => {
    await analytics.track("Onboarding", values);
    await AsyncStorage.setItem("onboarding_done", "true");
    // After a couple of seconds refetch the profile, to see if there are event credits
    setTimeout(refetchProfile, 2000);
    // dispatch({ type: "ONBOARDING_DONE" });
  };

  const setNewPassword = async (username, code, password) => {
    const uname = username.replace(" ", "+");

    await confirmResetPassword({
      username: uname,
      confirmationCode: code,
      newPassword: password,
    });
    await signOutAmplify();
    await signInAmplify({
      username: uname,
      password,
      options: {
        authFlowType: "USER_SRP_AUTH",
      },
    });

    await signIn({ email: uname, password });
  };

  const getOpenAppFromWebUrl = async (path = "/") => {
    // this.setState({ openingSettings: true });
    const session = await fetchAuthSessionWithLoginMismatchRetry({
      bypassCache: false, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
    });

    // Todo: refresh session?

    const linkData = {
      data: {
        idToken: session.tokens?.idToken?.toString(),
        accessToken: session.tokens?.accessToken?.toString(),
        username: session.tokens?.accessToken?.payload.username,
        $deeplink_path: path,
        golive: true,
        $og_title: t("Open in EventLive App"),
        $og_description: t("Get EventLive App to go Live"),
        $canonical_url: `https://broadcaster.eventlive.pro${path}`,
        // $og_image_url: "http://lorempixel.com/400/400",
      },
    };

    const link = await new Promise((resolve, reject) => {
      branch.link(linkData, function (err, link) {
        if (err) {
          reject(err);
        } else {
          resolve(link);
        }
      });
    });

    return link;
  };

  const legacyPricing =
    state.account_type === "business" ||
    !!state.legacy_pricing ||
    state.registered_datetime < "2023-07-15";

  return {
    checkUserExists,
    signIn,
    signInWithTokens,
    signInWithSocial,
    branchdata,
    signOut,
    signUp,
    setNewPassword,
    refetchProfile,
    finishOnboarding,
    getOpenAppFromWebUrl,
    setFirstLoad,
    legacyPricing,
    isSigningInWithTokens: state.isSigningInWithTokens,
    ...state,
    // Teams (PLAN.md §5.5): `me` is always your own profile (the fields
    // above); `workspace` is the account you're working in
    // (components/teams/useWorkspace.js). Event screens read the owner's
    // plan, account type and custom domain through useWorkspaceAuth().
    me: state,
    workspace,
  };
}

const getTokensFromQueryString = () => {
  try {
    let queryString = new URLSearchParams(window.location.search);
    const idToken = queryString.get("idToken");
    const accessToken = queryString.get("accessToken");
    const username = queryString.get("username");
    return { idToken, accessToken, username };
  } catch (err) {
    return {};
  }
};

export function ProvideAuth({ navigation, children }) {
  const tokens = getTokensFromQueryString(); // ensure they dont change as react-navigation redirects

  const auth = useProvideAuth(navigation, tokens);
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  return useContext(authContext);
};

// Add robust password generator
const generateStrongPassword = () => {
  const length = 16;
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
};
